/**
 * Phase 23-E side quest -- "Related Work" widget on the Home dashboard.
 *
 * Shows the top-N most-referenced entities across the whole platform
 * in the last 30 days. Deterministic aggregate over the backlinks
 * graph; zero LLM cost. Auto-hides when there are zero results (fresh
 * install) or the caller doesn't have permission.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  StickyNote, CheckSquare, CalendarClock, Building2, FileText,
  Wrench, Users, Network, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";

const ICON_BY_TYPE = {
  note: StickyNote, task: CheckSquare, meeting: CalendarClock,
  account: Building2, deal: FileText, quote: FileText, invoice: FileText,
  ticket: Wrench, employee: Users,
};

export default function RelatedWorkTile() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const user = useAuth((s) => s.user);
  const role = (user?.role || "").toUpperCase();
  const canView = ["CEO", "ADMIN", "MANAGER"].includes(role);

  useEffect(() => {
    if (!canView) { setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/backlinks/top-targets",
          { params: { days: 30, limit: 10 } });
        if (!cancelled) setItems(data?.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [canView]);

  if (!canView || !loaded || items.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md p-5 mb-4"
      data-testid="home-related-work-tile"
    >
      <header className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
          <Network className="h-4 w-4 text-emerald-300" strokeWidth={1.75} />
        </div>
        <div className="flex flex-col">
          <span className="font-heading text-[13px] uppercase tracking-widest">
            Most referenced (30d)
          </span>
          <span className="text-[11px] text-muted-foreground">
            Entities the team keeps coming back to
          </span>
        </div>
      </header>
      <ol className="space-y-1">
        {items.map((it, i) => {
          const Icon = ICON_BY_TYPE[it.target_type] || Network;
          return (
            <li key={`${it.target_type}:${it.target_id}`}>
              <Link
                to={it.path}
                className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/[0.05] transition-colors"
                data-testid={`related-work-row-${it.target_type}-${it.target_id}`}
              >
                <span className="w-5 text-[11px] text-muted-foreground text-right">{i + 1}.</span>
                <Icon className="h-4 w-4 opacity-70 shrink-0" strokeWidth={1.75} />
                <span className="flex-1 truncate text-sm">{it.title}</span>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {it.target_type}
                </span>
                <span className="text-xs font-mono text-emerald-300 ml-1">{it.count}×</span>
                <ArrowRight className="h-3 w-3 opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
